import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Modal,
  Icon,
  Text,
  Button,
  Contact,
  message,
  ListingEntry,
} from 'components';
import { useHistory } from 'react-router-dom';
import { useBreakpoint } from 'hooks';
import { AccessType } from '@bb/domain/dist/types';
import { Spin } from '@brokerbay/design-components';
import errorReporter from '@app/errorReporter';
import {
  sendDetails,
  coopConfirmCancel,
  coopExternalConfirmCancel,
  clientExternalConfirmCancel,
} from 'api/AppointmentController';
import { zPositions } from '@/constants';
import { useAppointmentActionModalContext } from 'contexts/modal/AppointmentActionModalContext';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { LEGACY_DIALOG_KEYS } from 'modals/keys';
import ConfirmDenySelector from './ConfirmDenySelector';
import { ActionType, CancelReason, BeforeAfter } from './types';
import { AccessTypeMap } from './constants';
import { CoopCancelReasonType } from '../../app/DashboardComponents/CoopCancelAppointmentComponent';

const successIcon = (
  <Icon type="checkCircle" color="success" width={22} height={22} />
);
const warningIcon = (
  <Icon type="antdExclamationCircle" color="warning" width={22} height={22} />
);

const AppointmentActionModalMap: Record<
  ActionType,
  {
    title: string;
    confirmButtonText: string;
    cancelButtonText: string;
    icon: React.ReactNode;
  }
> = {
  confirm: {
    title: "Are you sure you'd like to confirm this showing?",
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Back',
    icon: successIcon,
  },
  deny: {
    title: 'Are you sure you want to deny this showing?',
    confirmButtonText: 'Deny Showing',
    cancelButtonText: 'Back',
    icon: warningIcon,
  },
  cancel: {
    title: 'Are you sure you want to cancel this showing?',
    confirmButtonText: 'Cancel Showing',
    cancelButtonText: 'Back',
    icon: warningIcon,
  },
  deny_coop: {
    title: 'Are you sure you want to deny this time change suggestion?',
    confirmButtonText: 'Deny Showing',
    cancelButtonText: 'Back',
    icon: warningIcon,
  },
};

export const AppointmentActionModal = ({
  dashboardId,
}: {
  dashboardId?: string;
}) => {
  const {
    agent,
    action,
    visible,
    loadingAppointment,
    loadingAppointmentConfig,
    appointment,
    appointmentConfig,
    closeModal: _closeModal,
    onConfirm: _onConfirm,
    cancelFromTour: _cancelFromTour,
    onClose: _onClose,
    isMobileView,
  } = useAppointmentActionModalContext();
  const { isUsingNewArchitecture } = useDialogStore();
  const usingNewModalArch = isUsingNewArchitecture(
    LEGACY_DIALOG_KEYS.AppointmentActionModal,
  );
  const history = useHistory();
  const [cancelReason, setCancelReason] = useState<CancelReason>();
  const [details, setDetails] = useState('');
  const [initialDetails, setInitialDetails] = useState('');
  const [propertyAccess, setPropertyAccess] = useState<AccessType>();
  const [initialPropertyAccess, setInitialPropertyAccess] =
    useState<AccessType>();
  const [accessCode, setAccessCode] = useState('');
  const [initialAccessCode, setInitialAccessCode] = useState('');
  const [isOneDayCodeEnabled, setIsOneDayCodeEnabled] = useState(false);
  const [beforeAfter, setBeforeAfter] = useState<BeforeAfter>({
    beforeAfter: 'after',
    time: { hour: 12, minute: 0, am: false },
  });
  const [confirming, setConfirming] = useState(false);

  const { sm } = useBreakpoint();

  const closeModal = () => {
    if (_onClose) _onClose();
    _closeModal();
  };

  const isCancelOrDeny = ['cancel', 'deny'].includes(action);
  const canConfirm =
    !(
      isCancelOrDeny &&
      (!cancelReason || (cancelReason === 'custom' && !details))
    ) &&
    !(
      action === 'confirm' &&
      appointmentConfig?.listingAgentConfirm?.enabled &&
      !['Direct', 'Supra eKEY', 'SentriLock App'].includes(propertyAccess) &&
      !accessCode &&
      !details
    ) &&
    !loadingAppointmentConfig;

  const resetState = () => {
    setCancelReason(undefined);
    setDetails('');
    setAccessCode('');
    setBeforeAfter({
      beforeAfter: 'after',
      time: { hour: 12, minute: 0, am: false },
    });
  };

  const initializeAccessCode = (_accessCode: string) => {
    setInitialAccessCode(_accessCode);
    setAccessCode(_accessCode);
  };

  const initializePropertyAccess = (_propertyAccess: AccessType) => {
    setInitialPropertyAccess(_propertyAccess);
    setPropertyAccess(_propertyAccess);
  };

  const initializeDetails = (_details: string) => {
    setInitialDetails(_details);
    setDetails(_details);
  };

  useEffect(() => {
    if (!usingNewModalArch) {
      closeModal();
    }
  }, [history]);

  useEffect(() => {
    resetState();

    if (action === 'confirm' && appointment?.appointmentConfig?.access) {
      // If there's an existing appointmentConfig, use it to populate the form
      const { access } = appointment.appointmentConfig;
      const { instructionsKey, codeKey } = { ...AccessTypeMap[access] };
      const accessDetails =
        appointment.appointmentConfig.instructions[instructionsKey];

      const _accessCode =
        codeKey && accessDetails
          ? accessDetails[codeKey as keyof typeof accessDetails]
          : '';
      const _details = accessDetails?.details || '';

      setIsOneDayCodeEnabled(
        appointmentConfig?.instructions?.masterlock?.isOneDayCodeEnabled ??
          false,
      );
      initializePropertyAccess(access);
      initializeAccessCode(_accessCode);
      initializeDetails(_details);
    } else {
      initializePropertyAccess(undefined);
      initializeAccessCode('');
      initializeDetails('');
    }
  }, [action, appointment, appointmentConfig]);

  const onConfirm = async () => {
    let toastString: string;
    let errorToastString = 'Error';
    try {
      setConfirming(true);
      if (agent === 'coop' && (action === 'cancel' || action === 'confirm')) {
        const buildCoopCancelDetails = () => {
          switch (cancelReason) {
            case 'notInterested':
              return 'Client is no longer interested';
            case 'reschedule':
              return 'I will reschedule';
            default:
              return details;
          }
        };
        toastString =
          action === 'cancel' ? 'Showing cancelled' : 'Showing confirmed';
        errorToastString =
          action === 'cancel'
            ? 'Error cancelling showing'
            : 'Error confirming showing';

        if (dashboardId) {
          await coopExternalConfirmCancel({
            appointmentId: appointment.id,
            status: action,
            details: action === 'cancel' ? buildCoopCancelDetails() : undefined,
            isPending: cancelReason === 'custom',
            dashboardId,
          });
        } else if (_cancelFromTour) {
          await _cancelFromTour(
            (cancelReason !== 'custom'
              ? cancelReason
              : 'other') as CoopCancelReasonType,
            buildCoopCancelDetails(),
            _onConfirm,
          );
        } else {
          await coopConfirmCancel({
            appointmentId: appointment.id,
            status: action,
            details: action === 'cancel' ? buildCoopCancelDetails() : undefined,
          });
        }
      } else {
        const body: any = {};
        const buildInstructions = () => {
          switch (propertyAccess) {
            case 'Direct':
            case 'Key':
            case 'Supra eKEY':
              return {
                details,
              };
            case 'Supra Lockbox':
              return {
                ...appointment.appointmentConfig.instructions[
                  AccessTypeMap[propertyAccess].instructionsKey
                ],
                details,
              };
            case 'Door Code':
            case 'Lock Box':
              return {
                code: accessCode,
                details,
              };
            case 'MasterLock':
              return {
                deviceId: accessCode,
                isOneDayCodeEnabled,
                details,
              };
            case 'SentriLock':
            case 'SentriLockV2':
            case 'SentriLock App':
              return {
                ...appointment.appointmentConfig.instructions[
                  AccessTypeMap[propertyAccess].instructionsKey
                ],
                details,
              };
            default:
              throw new Error(`Unknown access type: ${propertyAccess}`);
          }
        };
        const buildDenyMessage = () => {
          switch (cancelReason) {
            case 'anotherTime':
              return `Please book for ${beforeAfter.beforeAfter} ${
                beforeAfter.time.hour
              }:${
                beforeAfter.time.minute === 0 ? '00' : beforeAfter.time.minute
              } ${beforeAfter.time.am ? 'AM' : 'PM'}`;
            case 'anotherDay':
              return 'Please book for another day';
            case 'custom':
              return details;
            default:
              throw new Error(`Unknown cancel reason: ${cancelReason}`);
          }
        };

        if (action === 'confirm') {
          toastString = 'Showing confirmed';
          errorToastString = 'Error confirming showing';

          body.notify = {
            ...appointment.notify,
          };
          body.details = {
            access: propertyAccess ?? '',
            instructions: {
              ...appointment.appointmentConfig.instructions,
            },
          };
          if (propertyAccess) {
            body.details.instructions[
              AccessTypeMap[propertyAccess].instructionsKey
            ] = buildInstructions();
          }
        } else if (action === 'cancel') {
          toastString = 'Showing cancelled';
          errorToastString = 'Error cancelling showing';

          body.details = {
            message: buildDenyMessage(),
          };

          if (dashboardId) {
            await clientExternalConfirmCancel({
              appointmentId: appointment.id,
              status: action,
              details: action === 'cancel' ? buildDenyMessage() : undefined,
              isPending: cancelReason === 'custom',
              dashboardId,
            });
          }
        } else if (action === 'deny_coop') {
          toastString = appointment.keepOriginalAppointment
            ? 'Time change suggestion denied'
            : 'Showing denied';
          errorToastString = 'Error denying showing';
          body.notify = {
            ...appointment.notify,
          };
          body.keepOriginalAppointment = appointment.keepOriginalAppointment;
          if (dashboardId) {
            await coopExternalConfirmCancel({
              dashboardId,
              appointmentId: appointment.id,
              status: 'cancel',
            });
          }
        } else if (action === 'deny') {
          toastString = 'Showing denied';
          errorToastString = 'Error denying showing';

          body.details = {
            message: buildDenyMessage(),
          };
          if (dashboardId) {
            await clientExternalConfirmCancel({
              appointmentId: appointment.id,
              status: action,
              details: action === 'deny' ? buildDenyMessage() : undefined,
              isPending: cancelReason === 'custom',
              dashboardId,
            });
          }
        }

        if (!dashboardId) {
          await sendDetails({
            detailsType: action,
            appointmentId: appointment.id,
            body,
            isPending: appointment.notify.office,
          });
        }
      }
    } catch (error) {
      toastString = undefined;
      message.error(errorToastString);
      errorReporter.error(
        `Error submitting appointment action modal: ${errorToastString}`,
        error,
      );
    } finally {
      if (!_cancelFromTour) {
        if (toastString) message.success(toastString);
        _onConfirm();
      }
      setConfirming(false);
      closeModal();
    }
  };

  const onCancel = () => {
    resetState();
    closeModal();
  };

  if (!action || !appointment || !visible) return null;

  const { title, icon, confirmButtonText, cancelButtonText } =
    AppointmentActionModalMap[action];

  const Loading = () => (
    <Centered>
      <StyledSpin />
    </Centered>
  );

  if (loadingAppointment)
    return (
      <StyledModal
        open={visible}
        closable={false}
        footer={null}
        sm={sm}
        isMobileView={isMobileView}
      >
        <Loading />
      </StyledModal>
    );

  return (
    <StyledModal
      zIndex={zPositions.tourModalSubModal}
      open={visible}
      closable={false}
      footer={null}
      sm={sm}
      isMobileView={isMobileView}
    >
      <ModalHeader>
        {icon}
        <TitleText>{title}</TitleText>
      </ModalHeader>

      <ModalBody>
        {agent === 'listing' && (
          <>
            <ContactContainer>
              <Contact
                contact={{
                  ...appointment.coop,
                  brokerageName: appointment.coop.org?.name,
                  firstName:
                    appointment.coop.firstName || appointment.coop.name,
                }}
                hoverable={false}
                disableClick
                showBrokerageName
              />
            </ContactContainer>

            <ListingEntry
              listingId={appointment.listingId}
              line1={appointment.listing.line1}
              line2={appointment.listing.line2}
              time={appointment.time}
              timeEnd={appointment.timeEnd}
              thumbnail={appointment.listing.thumbnail}
              type={'modal'}
            />

            {action === 'confirm' &&
              appointment.covid19RequestedAck &&
              !appointment.covid19Signed &&
              appointment.orgHasCovidFlag && (
                <StyledCovidDisclaimer>
                  The showing agent will be required to sign a COVID-19
                  acknowledgement form on this showing. They will not receive
                  the confirmation until it is signed.
                </StyledCovidDisclaimer>
              )}
          </>
        )}

        {agent === 'coop' && action === 'deny_coop' && (
          <StyledSubtext>
            {`If denied, the original showing request will be ${
              appointment.keepOriginalAppointment ? 'kept' : 'denied'
            }.`}
          </StyledSubtext>
        )}

        {(isCancelOrDeny || (action === 'confirm' && agent === 'listing')) && (
          <>
            {loadingAppointmentConfig ? (
              <Loading />
            ) : (
              (isCancelOrDeny ||
                appointmentConfig?.listingAgentConfirm?.enabled) && (
                <ConfirmDenySelector
                  beforeAfter={beforeAfter}
                  setBeforeAfter={setBeforeAfter}
                  cancelReason={cancelReason}
                  setCancelReason={setCancelReason}
                  propertyAccess={{
                    value: propertyAccess,
                    initial: initialPropertyAccess,
                    set: setPropertyAccess,
                  }}
                  details={{
                    value: details,
                    initial: initialDetails,
                    set: setDetails,
                  }}
                  accessCode={{
                    value: accessCode,
                    initial: initialAccessCode,
                    set: setAccessCode,
                  }}
                  isOneDayCodeEnabled={{
                    value: isOneDayCodeEnabled,
                    set: setIsOneDayCodeEnabled,
                  }}
                  isCancelOrDeny={isCancelOrDeny}
                  canConfirm={canConfirm}
                />
              )
            )}
          </>
        )}
      </ModalBody>

      <ModalActions>
        <ActionButton onClick={onCancel} data-cy="reset-page-mode">
          {cancelButtonText}
        </ActionButton>
        <ActionButton
          onClick={onConfirm}
          disabled={!canConfirm || confirming}
          type="primary"
          loading={confirming}
          data-cy={`send-${action}`}
        >
          {confirmButtonText}
        </ActionButton>
      </ModalActions>
    </StyledModal>
  );
};

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 16px;
`;

const StyledSpin = styled(Spin)`
  margin-left: 16px;
`;

const StyledModal = styled(Modal)<{ isMobileView?: boolean; sm?: boolean }>`
  max-width: min(416px, 95vw);
  & .ant-modal-body {
    padding: ${({ sm }) =>
      !sm ? '16px 12px 12px 12px' : '32px 32px 24px 32px'};
  }
  ${({ isMobileView }) =>
    isMobileView
      ? `
      bottom: 0px;
      top: auto;
      margin-bottom: 0px;
      padding-bottom: 0px;
      min-width: 100%;
      max-width: 100%;
      position: absolute;
      `
      : `
      min-width: 416px;
      max-width: 29vw;
      position: relative;
      `}
`;

const ContactContainer = styled.div`
  margin: 16px 0 16px 0;
`;

const TitleText = styled(Text)`
  color: #000000d9;
  font-size: 16px;
  font-weight: 600;
  margin-left: 16px;
`;

const ModalBody = styled.div`
  margin-top: 8px;
  margin-left: 38px;
  margin-bottom: 24px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled(Button)`
  margin-left: 8px;
`;

const StyledCovidDisclaimer = styled.div`
  margin: 16px 0 16px 0;
  color: #000000a6;
`;

const StyledSubtext = styled.div`
  margin: 8px 0 8px 0;
  color: #000000a6;
`;

const ModalHeader = styled.div`
  display: flex;
`;
